import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { reportService } from '../../services/reportService';
import { fleetService } from '../../services/fleetService';
import RevenueReportChart from '../../components/reports/RevenueReportChart';
import { FileText, Filter, Calendar, Download } from 'lucide-react';

const RevenueReportsPage = () => {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [revenueData, setRevenueData] = useState([]);
    const [fleets, setFleets] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        period: 'monthly',
        fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
        start_date: new Date(new Date().getFullYear(), new Date().getMonth() - 5, 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
    });
    const [summary, setSummary] = useState({
        total_revenue: 0,
        total_orders: 0,
        average_fare: 0,
        highest_day: {
            date: '',
            revenue: 0
        },
        growth: 0
    });

    useEffect(() => {
        fetchRevenueData();
        if (user.user_type === 'admin') {
            fetchFleets();
        }
    }, [filterOptions]);

    const fetchRevenueData = async () => {
        setIsLoading(true);
        try {
            const data = await reportService.getRevenueReport(
                filterOptions.fleet_id,
                filterOptions.period,
                filterOptions.start_date,
                filterOptions.end_date
            );

            setRevenueData(data.chart_data);
            setSummary(data.summary);
        } catch (error) {
            console.error('Error fetching revenue data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterOptions(prev => ({ ...prev, [name]: value }));
    };

    const handleDateRangeChange = (startDate, endDate) => {
        setFilterOptions(prev => ({
            ...prev,
            start_date: startDate,
            end_date: endDate
        }));
    };

    const handleExportReport = async () => {
        try {
            await reportService.exportRevenueReport(
                filterOptions.fleet_id,
                filterOptions.period,
                filterOptions.start_date,
                filterOptions.end_date,
                'excel' // 可選 'excel', 'csv', 'pdf'
            );
        } catch (error) {
            console.error('Error exporting report:', error);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">收入報表</h1>
                <button
                    onClick={handleExportReport}
                    className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <Download className="w-5 h-5 mr-2" />
                    匯出報表
                </button>
            </div>

            {/* 篩選器 */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                    <Filter className="w-5 h-5 text-gray-500 mr-2" />
                    <h3 className="font-bold">篩選條件</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            統計週期
                        </label>
                        <select
                            name="period"
                            value={filterOptions.period}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="daily">每日</option>
                            <option value="weekly">每週</option>
                            <option value="monthly">每月</option>
                            <option value="yearly">每年</option>
                        </select>
                    </div>

                    {user.user_type === 'admin' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                車隊
                            </label>
                            <select
                                name="fleet_id"
                                value={filterOptions.fleet_id}
                                onChange={handleFilterChange}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                                <option value="">所有車隊</option>
                                {fleets.map(fleet => (
                                    <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                        {fleet.fleet_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            開始日期
                        </label>
                        <input
                            type="date"
                            name="start_date"
                            value={filterOptions.start_date}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            結束日期
                        </label>
                        <input
                            type="date"
                            name="end_date"
                            value={filterOptions.end_date}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>
                </div>
            </div>

            {/* 收入摘要卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-gray-500">總收入</h3>
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <FileText className="w-5 h-5 text-green-500" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold">${summary.total_revenue.toLocaleString()}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                        <span>共 {summary.total_orders.toLocaleString()} 筆訂單</span>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-gray-500">平均車資</h3>
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-500" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold">${summary.average_fare.toLocaleString()}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                        <span>每筆訂單平均</span>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-gray-500">收入最高日</h3>
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-purple-500" />
                        </div>
                    </div>
                    <p className="text-2xl font-bold">${summary.highest_day.revenue.toLocaleString()}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                        <span>{summary.highest_day.date}</span>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-gray-500">成長比例</h3>
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                            <FileText className="w-5 h-5 text-yellow-500" />
                        </div>
                    </div>
                    <p className={`text-2xl font-bold ${summary.growth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {summary.growth >= 0 ? '+' : ''}{summary.growth.toFixed(1)}%
                    </p>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                        <span>與上一期相比</span>
                    </div>
                </div>
            </div>

            {/* 收入圖表 */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="font-bold mb-4">收入趨勢</h3>
                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="h-96">
                        <RevenueReportChart data={revenueData} period={filterOptions.period} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default RevenueReportsPage;